import ArticleLayout from './components/ArticleLayout';
import { articlesMeta } from '@/mocks/articlesData';

const data = articlesMeta.find((a) => a.slug === 'is-sugargoo-legit')!;

export default function IsSugargooLegit() {
  return (
    <ArticleLayout data={data}>
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Introduction: Addressing the Big Question
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          If you are considering using Sugargoo for the first time, the question <strong>&quot;Is Sugargoo legit?&quot;</strong> is probably at the top of your mind. It is a completely valid concern — you are sending money overseas to a platform you may have only recently discovered, trusting them to buy products on your behalf, inspect them, and ship them internationally.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          This comprehensive safety analysis examines Sugargoo from every angle: business legitimacy, operational transparency, payment security, buyer protection mechanisms, user reputation, and risk factors. Our goal is to give you the facts you need to make an informed decision.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          <strong>Important disclaimer:</strong> This analysis is based on publicly available information, user reports, and platform features as of 2026. Shopping through any international agent involves inherent risks, and no service can guarantee 100% protection against every possible issue.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Is Sugargoo a Legitimate Business?
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          <strong>Yes, Sugargoo is a legitimate registered business</strong> operating as an international shopping agent. The platform has been active for several years and has processed hundreds of thousands of orders for customers worldwide. Key indicators of legitimacy include:
        </p>
        <div className="space-y-3 mb-4">
          {[
            'Established operational presence with a physical warehouse facility in China',
            'Registered business entity with proper licensing for cross-border commerce',
            'Active customer service infrastructure with multiple support channels',
            'Transparent fee structure published on the platform',
            'Processing payments through established, trackable methods',
            'Documented history of order fulfillment and dispute resolution',
          ].map((item, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                <i className="ri-check-line w-3 h-3 flex items-center justify-center"></i>
              </div>
              <span className="text-sm text-foreground-700">{item}</span>
            </div>
          ))}
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          However, legitimacy does not mean perfection. Like any business handling complex international logistics, Sugargoo has its share of customer complaints and negative experiences. The question is not whether problems exist — they do, at every agent — but whether the platform handles them fairly and transparently.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          How Sugargoo Protects Buyers
        </h2>
        <div className="space-y-4">
          {[
            {
              title: 'Warehouse QC Inspection',
              desc: 'Every item is photographed in detail upon arrival at the warehouse. This creates a documented record of condition before international shipping, giving you the chance to identify problems and request returns while the item is still in China.',
            },
            {
              title: 'Order Tracking System',
              desc: 'All orders are tracked through the platform dashboard from purchase through warehouse arrival to international shipping. This transparency lets you monitor progress and identify delays early.',
            },
            {
              title: 'Payment Escrow Model',
              desc: 'Your payment is held by the platform until the seller ships and the item arrives at the warehouse. This prevents sellers from taking money without delivering, a common scam protection feature.',
            },
            {
              title: 'Return & Refund Process',
              desc: 'Defective or incorrect items can be returned to sellers within the return window. While not instantaneous, this process does provide a path to resolution for warehouse-discovered issues.',
            },
            {
              title: 'Customer Support Channels',
              desc: 'Sugargoo offers multiple support options including live chat, ticket systems, and email. Response times vary but most users report replies within 24 hours for standard inquiries.',
            },
            {
              title: 'Community Accountability',
              desc: 'As a platform with public user communities, negative experiences get shared widely. This creates market pressure for Sugargoo to maintain service quality and address complaints promptly.',
            },
          ].map((protection) => (
            <div key={protection.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 shrink-0 mt-0.5">
                <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{protection.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{protection.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Common Risks and How to Manage Them
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          No international shopping agent is risk-free. Here are the most common risk categories and practical ways to protect yourself:
        </p>
        <div className="space-y-4">
          {[
            {
              risk: 'Seller Scams or Non-Delivery',
              mitigation: 'Use Sugargoo escrow payment protection. Never pay sellers directly outside the platform. Check seller ratings when available.',
            },
            {
              risk: 'Quality Does Not Match Photos',
              mitigation: 'Rely on QC warehouse photos, not seller listing images. Use a Sugargoo Spreadsheet with community quality notes for realistic expectations.',
            },
            {
              risk: 'Shipping Loss or Damage',
              mitigation: 'Choose shipping methods with insurance options. Consider declared value carefully. Document package condition on arrival.',
            },
            {
              risk: 'Unexpected Customs Charges',
              mitigation: 'Research your country import duties before ordering. Declare accurate but reasonable values. Budget for potential VAT/duty charges.',
            },
            {
              risk: 'Delayed Deliveries',
              mitigation: 'Use tracked shipping methods. Build buffer time into urgent orders. Monitor tracking and contact support if packages stall for weeks.',
            },
            {
              risk: 'Account or Payment Issues',
              mitigation: 'Use secure payment methods with buyer protection. Enable two-factor authentication. Keep records of all transactions and communications.',
            },
          ].map((item) => (
            <div key={item.risk} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0 mt-0.5">
                <i className="ri-alert-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{item.risk}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed"><strong>Mitigation:</strong> {item.mitigation}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          What Real Users Say: Reputation Analysis
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Analyzing thousands of user reviews across community forums, social media, and dedicated review platforms reveals a consistent pattern:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="bg-background-100/60 rounded-xl p-4 md:p-5 border border-background-200/60">
            <h4 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
              <i className="ri-thumb-up-line w-4 h-4 flex items-center justify-center text-accent-500"></i>
              Positive Themes
            </h4>
            <ul className="space-y-2">
              {[
                'QC photos are detailed and reliable',
                'Platform interface is modern and easy to use',
                'Shipping options are flexible and well-priced',
                'Consolidation saves significant money',
                'Support is generally responsive and helpful',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-check-line w-3 h-3 flex items-center justify-center text-accent-500 mt-0.5 shrink-0"></i>
                  <span className="text-xs text-foreground-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-background-100/60 rounded-xl p-4 md:p-5 border border-background-200/60">
            <h4 className="text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
              <i className="ri-information-line w-4 h-4 flex items-center justify-center text-secondary-500"></i>
              Common Complaints
            </h4>
            <ul className="space-y-2">
              {[
                'Shipping costs higher than initial estimates',
                'Occasional delays during peak seasons',
                'Return process can be slow with some sellers',
                'Customs issues vary by destination country',
                'Communication gaps during complex disputes',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-close-line w-3 h-3 flex items-center justify-center text-secondary-500 mt-0.5 shrink-0"></i>
                  <span className="text-xs text-foreground-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          The overall sentiment is <strong>predominantly positive</strong> among users who understand the agent model and set realistic expectations. Negative experiences are most common among first-time users who expected domestic e-commerce convenience without the inherent complexity of cross-border shopping.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Red Flags: When to Be Cautious
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          While Sugargoo itself is legitimate, there are situations where extra caution is warranted:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Requests to pay outside the official platform — always use Sugargoo official payment system',
            'Deals that seem too good to be true from unknown sellers — extreme discounts often signal scams',
            'Sellers with no history or ratings on the marketplace — higher risk of non-delivery',
            'Shipping to addresses that are not your own — increases complications if issues arise',
            'Orders with prohibited items — customs seizure can result in total loss with no refund',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center text-secondary-500 mt-0.5 shrink-0"></i>
              <span className="text-sm text-foreground-700">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Most problems are avoidable with common-sense precautions: stick to the official platform, research sellers, start with small test orders, and never bypass payment protections.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Final Verdict: Is Sugargoo Safe to Use?
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          <strong>Sugargoo is a legitimate, operational shopping agent platform</strong> with established buyer protection mechanisms, a transparent warehouse process, and generally positive user feedback. It is not a scam, nor is it a fly-by-night operation.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          That said, &quot;safe&quot; is relative to your expectations and shopping habits. Sugargoo is safe for buyers who:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Understand that international shipping involves inherent delays and variability',
            'Accept that QC photos are the main quality protection, not guarantees',
            'Budget realistically for shipping, customs, and potential duties',
            'Start with small orders to learn the process before committing large amounts',
            'Use community resources like Sugargoo Spreadsheets for informed product selection',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5 shrink-0"></i>
              <span className="text-sm text-foreground-700">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          If you need guaranteed delivery dates, hassle-free returns, or zero-risk purchasing, no international agent — including Sugargoo — will fully meet those expectations. But for shoppers willing to navigate the agent model trade-offs, Sugargoo offers a legitimate, functional, and widely trusted platform.
        </p>
      </section>
    </ArticleLayout>
  );
}